
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import Hero from './components/Hero';
import ProductShowcase from './components/ProductShowcase';
import Benefits from './components/Benefits';
import Process from './components/Process';
import UseCases from './components/UseCases';
import FAQ from './components/FAQ';
import CallToActionSection from './components/CallToActionSection';
import { getLocalizedContent } from './utils/localization';
import { updateMetaTags, generateArticleSchema } from '@/utils/seoUtils';

const ProductVideosPage = () => {
  const { currentLanguage } = useLanguage();
  const { lang } = useParams<{ lang: string }>();
  const t = translations[currentLanguage];
  const content = getLocalizedContent(currentLanguage);
  
  useEffect(() => {
    // Define SEO metadata based on current language
    let pageTitle = "";
    let pageDescription = "";
    
    switch(currentLanguage) {
      case 'de':
        pageTitle = "KI-Produktvideos für überzeugende Präsentationen | Future Level";
        pageDescription = "Automatisierte Produktvideos mit KI-Technologie – Präsentieren Sie Ihre Produkte optimal, sparen Sie Zeit und steigern Sie die Conversion-Rate.";
        break;
      case 'en':
        pageTitle = "AI Product Videos for Compelling Presentations | Future Level";
        pageDescription = "Automated product videos with AI technology – Present your products optimally, save time, and increase conversion rates.";
        break;
      case 'fr':
        pageTitle = "Vidéos de produits IA pour des présentations convaincantes | Future Level";
        pageDescription = "Vidéos de produits automatisées avec technologie IA – Présentez vos produits de manière optimale, gagnez du temps et augmentez les taux de conversion.";
        break;
      case 'it':
        pageTitle = "Video di prodotti con IA per presentazioni convincenti | Future Level";
        pageDescription = "Video di prodotti automatizzati con tecnologia IA – Presenta i tuoi prodotti in modo ottimale, risparmia tempo e aumenta i tassi di conversione.";
        break;
      default:
        pageTitle = `${t.productVideos} | Future Level`;
        pageDescription = t.productVideosDesc;
    }
    
    const canonicalUrl = window.location.href;
    
    // Update all meta tags including Open Graph and Twitter
    updateMetaTags({
      title: pageTitle,
      description: pageDescription,
      keywords: `${t.productVideos}, ${t.videoMarketing}, AI, ${t.kiVideo}, E-Commerce, Future Level`,
      canonicalUrl,
      ogImage: "https://future-level.ch/opengraph-image-p98pqg.png",
      ogType: "website",
      language: currentLanguage
    });

    // Add schema.org structured data for better SEO
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      'name': pageTitle,
      'description': pageDescription,
      'provider': {
        '@type': 'Organization',
        'name': 'Future Level',
        'url': 'https://future-level-video-craft.lovable.app',
        'logo': {
          '@type': 'ImageObject',
          'url': 'https://i.imgur.com/qvgtBeK.png',
          'width': '180',
          'height': '60'
        }
      },
      'serviceType': 'AI Video Production',
      'areaServed': {
        '@type': 'Country',
        'name': 'Switzerland'
      },
      'offers': {
        '@type': 'Offer',
        'description': pageDescription,
        'price': '0',
        'priceCurrency': 'CHF'
      }
    });
    
    // Remove existing schema if any
    const existingSchema = document.getElementById('product-videos-schema');
    if (existingSchema) {
      existingSchema.remove();
    }
    
    script.id = 'product-videos-schema';
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [currentLanguage, t, content]);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow">
        <Hero t={t} content={content} />
        <ProductShowcase content={content} />
        <Benefits content={content} />
        <Process content={content} />
        <UseCases content={content} />
        <FAQ content={content} />
        <CallToActionSection t={t} content={content} currentLanguage={currentLanguage} />
      </main>
      <Footer />
    </div>
  );
};

export default ProductVideosPage;
